// Composer attachments: drives the same presigned upload flow the Media page
// uses (createMedia → PUT → complete), but tuned for chat — per-file preview,
// remove, and a reset after send. Kept separate from pages/media/useUploads so
// the Media page's semantics (its own list, clearFinished) stay untouched.

import { useCallback, useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { createMedia, completeMedia } from '../api/media.ts';
import { uploadToUrl } from '../api/client.ts';
import { performUpload, inferMediaKind } from '../lib/upload.ts';
import type { Attachment } from './types.ts';

export interface UseAttachments {
  items: Attachment[];
  add: (files: File[]) => void;
  remove: (localId: string) => void;
  reset: () => void;
  /** True while any upload is still in flight — the composer blocks send. */
  uploading: boolean;
  /** media_ids of completed uploads, in add order — sent with the message. */
  mediaIds: string[];
}

export function useAttachments(): UseAttachments {
  const qc = useQueryClient();
  const [items, setItems] = useState<Attachment[]>([]);
  // Track object URLs so we can revoke them and avoid leaks.
  const urlsRef = useRef<string[]>([]);

  const patch = (localId: string, p: Partial<Attachment>) =>
    setItems((prev) => prev.map((i) => (i.localId === localId ? { ...i, ...p } : i)));

  const add = useCallback(
    (files: File[]) => {
      for (const file of files) {
        const localId = crypto.randomUUID();
        const kind = inferMediaKind(file);
        if (!kind) {
          setItems((prev) => [
            ...prev,
            {
              localId,
              name: file.name,
              progress: 0,
              phase: 'error',
              error: 'Images and video only',
            },
          ]);
          continue;
        }
        const previewUrl = kind === 'image' ? URL.createObjectURL(file) : undefined;
        if (previewUrl) urlsRef.current.push(previewUrl);
        setItems((prev) => [
          ...prev,
          { localId, name: file.name, previewUrl, progress: 0, phase: 'uploading' },
        ]);
        void performUpload({
          file,
          kind,
          deps: { createMedia, uploadToUrl, completeMedia },
          onPhase: (p) => {
            if (p.phase === 'uploading') patch(localId, { phase: 'uploading', progress: p.progress ?? 0 });
            else if (p.phase === 'done')
              patch(localId, { phase: 'done', progress: 1, mediaId: p.mediaId });
            else if (p.phase === 'error') patch(localId, { phase: 'error', error: p.error });
            else if (p.mediaId) patch(localId, { mediaId: p.mediaId });
          },
        })
          .then(() => qc.invalidateQueries({ queryKey: ['media'] }))
          .catch(() => {
            /* phase already 'error' via onPhase */
          });
      }
    },
    [qc],
  );

  const remove = useCallback((localId: string) => {
    setItems((prev) => prev.filter((i) => i.localId !== localId));
  }, []);

  const reset = useCallback(() => {
    urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
    urlsRef.current = [];
    setItems([]);
  }, []);

  useEffect(() => {
    return () => urlsRef.current.forEach((u) => URL.revokeObjectURL(u));
  }, []);

  const uploading = items.some((i) => i.phase === 'uploading');
  const mediaIds = items.filter((i) => i.phase === 'done' && i.mediaId).map((i) => i.mediaId!);

  return { items, add, remove, reset, uploading, mediaIds };
}
